// @owners { team: patients-team }
import {
  Body,
  Button,
  Card,
  LgPadding,
  LoadingEmptyState,
  SecondaryPage,
  Toast,
  ToastContext,
  XlPadding,
} from '@alto/design-system';
import { useQueryInsuranceConfigs } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import { useQueryClient } from '@tanstack/react-query';
import React, { useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
// eslint-disable-next-line import/no-deprecated
import { createPhotoInsurance } from '~shared/actions/insurances';
// eslint-disable-next-line import/no-deprecated
import { setEditingInsuranceID, updateOnboardingUser } from '~shared/actions/onboarding';
import { getVerifyFailureErrorMessage } from '~shared/constants';
import getIsPhotoUpload from '~shared/features/insurances/helpers/getIsPhotoUpload';
import {
  INSURANCE_INPUT_PHOTO,
  ONBOARD_STATE_INSURANCE_INPUT,
} from '~shared/features/onboarding/helpers/onboardingRouteHandler';
import { routeFromPath } from '~shared/features/onboarding/helpers/routeToPath';
import { getEditingOnboardingInsuranceID } from '~shared/features/onboarding/selectors/getOnboarding';
import getShowProgynySelfServiceForUser from '~shared/features/users/selectors/getShowProgynySelfServiceForUser';
import { getErrorMessage } from '~shared/helpers/helper';
import { useAnalytics } from '~shared/hooks/useAnalytics';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { queries } from '~shared/queries/query-keys';
import { useGetInsuranceByID } from '~shared/queries/query-keys/insurances';
import getInsuranceError from '~shared/selectors/ui/errors/getInsuranceError';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type Insurance } from '~shared/types';
import { OnboardingNavBarLogo } from './OnboardingNavBarLogo';
import { useNavigationContext } from '~web/contexts';
import { InsurancePhotoUploadButtons } from '~web/features/insurances/components/InsurancePhotoUploadButtons';
import { OnboardingHeader } from '~web/features/onboarding/components/redesign/OnboardingHeader';
import { nextUrl, previousUrl } from '~web/features/onboarding/helpers/urlHandler';

const OnboardingInsuranceInputPhoto = () => {
  const queryClient = useQueryClient();
  const { trackEvent, trackPageView } = useAnalytics();
  const dispatch = useDispatchShared();
  const { addToast } = useContext(ToastContext);
  const { navigate } = useNavigation();
  const location = useLocation();
  const currentRoute = routeFromPath(location.pathname);
  const editingInsuranceID = useSelectorShared(getEditingOnboardingInsuranceID);
  const onboardingPreviousUrl = previousUrl({ params: { editingInsuranceID }, currentRoute });
  const loading = useSelectorShared((state) => state.ui.loading.createPhotoInsuranceLoading);
  const { previous: previousWebUrl } = useNavigationContext();
  const origin = previousWebUrl ? routeFromPath(previousWebUrl) : '';
  const insuranceID = useSelectorShared(getEditingOnboardingInsuranceID);
  const { insurance } = useGetInsuranceByID(insuranceID);
  const viewOnly = getIsPhotoUpload(insurance);
  const showProgyny = useSelectorShared(getShowProgynySelfServiceForUser);
  const nextRoute = nextUrl({ currentRoute: INSURANCE_INPUT_PHOTO });
  const previousRoute = previousUrl({ params: { editingInsuranceID }, currentRoute: INSURANCE_INPUT_PHOTO });
  const error = useSelectorShared(getInsuranceError);
  const { headerBackgroundColor, headerLogoUrl, isFetching, partnershipName } = useQueryInsuranceConfigs();

  const title = showProgyny ? 'Take a photo of your insurance' : 'Take a photo of your benefits card';
  const subTitle = showProgyny
    ? 'Make sure to include both sides of your insurance card and take the photos on a neutral background.'
    : 'Include both sides of your card, and take the photos on a neutral background.';

  const onBack = () => {
    if (onboardingPreviousUrl) {
      navigate(onboardingPreviousUrl);
    }
  };

  type Values = {
    image_url: File[];
    image_url_2: File[];
  };

  const onSave = async (newInsurance: Values) => {
    const frontImage = newInsurance.image_url[0] ?? insurance?.image_url;
    const backImage = newInsurance.image_url_2[0] ?? insurance?.image_url_2;
    if (!frontImage || !backImage) {
      addToast(<Toast variant="error">Please include both sides of your card.</Toast>);
      return;
    }
    const imageURLs = [frontImage, backImage];

    const filteredImageURLs = imageURLs.filter(Boolean);
    // @ts-expect-error I'm pretty convinced we're actually using createPhotoInsurance wrong here but it's out-of-scope
    // of this PR to fix it.
    // eslint-disable-next-line import/no-deprecated
    const savedInsurance: Insurance = await dispatch(createPhotoInsurance(filteredImageURLs));
    if (!savedInsurance) {
      addToast(<Toast variant="error">{getErrorMessage(error) || getVerifyFailureErrorMessage()}</Toast>);
      return;
    }
    if (!insurance) {
      trackEvent({
        event: EVENTS.INSURANCE_ADDED,
        params: {
          source: 'onboarding',
          type: 'photo',
          entity_name: savedInsurance.insurance_plan_name ?? '',
        },
      });
    }
    queryClient.invalidateQueries({ queryKey: queries.insurances.fetchAll._def });
    dispatch(setEditingInsuranceID(null));
    navigate(nextRoute);
  };

  const onCancel = () => {
    dispatch(setEditingInsuranceID(null));
    if (previousRoute) {
      navigate(previousRoute);
    }
  };

  useEffect(() => {
    dispatch(
      // eslint-disable-next-line import/no-deprecated
      updateOnboardingUser({
        onboard_state: ONBOARD_STATE_INSURANCE_INPUT,
      }),
    );

    // Unset editing insurance ID if user navigates away using the back button, instead of save or cancel button
    return () => {
      dispatch(setEditingInsuranceID(null));
    };
  }, [dispatch]);

  useEffect(() => {
    trackPageView({
      event: EVENTS.ONBOARDING__INSURANCE_INPUT_PHOTO_VIEWED,
      params: {
        origin,
        is_progyny_rx_view: showProgyny,
      },
    });
  }, [origin, showProgyny, trackPageView]);

  const addBenefitButtonText = showProgyny ? 'Save insurance' : 'Save benefit';

  const { handleSubmit, setValue } = useForm<Values>({
    defaultValues: { image_url: [], image_url_2: [] },
    mode: 'onSubmit',
  });

  if (isFetching) {
    return (
      <XlPadding>
        <LoadingEmptyState />
      </XlPadding>
    );
  }

  return (
    <SecondaryPage
      headerBackgroundColor={headerBackgroundColor}
      withoutWebFramingElementHeights
      dismissIcon="chevronleft"
      onDismiss={onBack}
      NavBarCenterContent={
        <OnboardingNavBarLogo
          illustrationSrc={headerLogoUrl}
          partnershipName={partnershipName}
        />
      }
      HeaderContent={
        <OnboardingHeader
          title={title}
          subtitle={<Body center>{subTitle}</Body>}
        />
      }
      buttons={[
        <Button
          label="Cancel"
          onPress={onCancel}
          type="tertiary"
          key="cancel button"
          loading={loading}
        />,
        <Button
          label={addBenefitButtonText}
          onPress={handleSubmit(onSave)}
          loading={loading}
          key="submit button"
          disabled={viewOnly}
        />,
      ]}
      footerPlacementContext="webScreenWithoutPolicyFooter"
    >
      <Card>
        <LgPadding>
          <InsurancePhotoUploadButtons
            loading={loading}
            onFormChange={(name, file) => {
              setValue(name, file);
            }}
            initialFrontImageUrl={insurance?.image_url ?? ''}
            initialBackImageUrl={insurance?.image_url_2 ?? ''}
            disabled={viewOnly}
          />
        </LgPadding>
      </Card>
    </SecondaryPage>
  );
};

export default OnboardingInsuranceInputPhoto;
